import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

const BI = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/icons';
const STEPS = [
  { id: 'verify_email', label: 'Verify your email', desc: 'Confirm your email address.', url: null, icon: `${BI}/envelope-check.svg` },
  { id: 'link_discord', label: 'Link Discord', desc: 'Manage your servers and Luna.', url: '/auth/teknik/link-discord', icon: `${BI}/discord.svg` },
  { id: 'link_minecraft', label: 'Link Minecraft', desc: 'Connect your Minecraft account for stats.', url: '/account', icon: `${BI}/controller.svg` },
  { id: 'set_avatar', label: 'Upload a profile picture', desc: 'Shown on your public profile.', url: '/account', icon: `${BI}/person-circle.svg` },
  // Only teknik.sh destinations that exist: Minecraft hosting is kohosting.app,
  // and there has never been a /minecraft route here to land on.
  { id: 'explore', label: 'Explore Teknik', desc: 'Cloud, Books and the Store.', url: '/store', icon: `${BI}/compass.svg` },
];

@customElement('onboarding-checklist')
export class OnboardingChecklist extends LitElement {
  @state() completed: string[] = [];
  @state() dismissed = false;
  @state() authStatus: any = null;

  private readonly STORAGE_KEY = 'teknik_onboarding';

  async connectedCallback() {
    super.connectedCallback();
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved) {
      const { completed, dismissed } = JSON.parse(saved);
      this.completed = completed || [];
      this.dismissed = dismissed || false;
    }
    try {
      const res = await fetch('/api/auth/status', { credentials: 'include' });
      this.authStatus = await res.json();
      // Auto-mark steps based on auth status
      const done = [...this.completed];
      if (this.authStatus?.user?.emailVerified && !done.includes('verify_email')) done.push('verify_email');
      if (this.authStatus?.discordLinked && !done.includes('link_discord')) done.push('link_discord');
      if (this.authStatus?.user?.minecraftUsername && !done.includes('link_minecraft')) done.push('link_minecraft');
      if (this.authStatus?.user?.avatar && !done.includes('set_avatar')) done.push('set_avatar');
      this.completed = done;
      this.save();
    } catch {}
  }

  save() {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ completed: this.completed, dismissed: this.dismissed }));
  }

  dismiss() {
    this.dismissed = true;
    this.save();
  }

  markDone(id: string) {
    if (!this.completed.includes(id)) {
      this.completed = [...this.completed, id];
      this.save();
    }
  }

  get visibleSteps() {
    // Hide verify_email step if already verified
    if (this.authStatus?.user?.emailVerified) return STEPS.filter(s => s.id !== 'verify_email');
    return STEPS;
  }
  get progress() { return this.visibleSteps.filter(s => this.completed.includes(s.id)).length; }
  get total() { return this.visibleSteps.length; }
  get allDone() { return this.progress >= this.total; }

  static styles = css`
    :host { display: block; }
    .checklist {
      background: var(--tk-accent-soft); border: 1px solid var(--tk-accent-line);
      border-radius: 14px; padding: 1.2rem; margin-bottom: 12px;
    }
    .checklist-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: .8rem; }
    .checklist-title { font-size: .95rem; font-weight: 700; color: var(--tk-accent); }
    .checklist-prog { font-size: .78rem; color: var(--tk-dim); }
    .progress-bar { height: 4px; background: var(--tk-border); border-radius: 2px; margin-bottom: 1rem; overflow: hidden; }
    .progress-fill { height: 100%; background: linear-gradient(90deg, var(--tk-accent), var(--tk-accent)); border-radius: 2px; transition: width .4s ease; }
    .step { display: flex; align-items: center; gap: 10px; padding: .45rem 0; cursor: pointer; }
    .step-check { width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--tk-border); flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: all .15s; }
    .step-check.done { background: var(--tk-accent); border-color: var(--tk-accent); }
    .step-check.done::after { content: '✓'; font-size: .7rem; color: white; }
    .step-icon { font-size: 1rem; }
    .step-info { flex: 1; min-width: 0; }
    .step-label { font-size: .85rem; font-weight: 600; color: var(--tk-text); text-decoration: none; }
    .step-label.done { color: var(--tk-dim); text-decoration: line-through; }
    .step-desc { font-size: .74rem; color: var(--tk-dim); }
    .dismiss-btn { background: none; border: none; color: var(--tk-dim); cursor: pointer; font-size: .8rem; padding: 0; }
    .dismiss-btn:hover { color: var(--tk-dim); }
    .all-done { text-align: center; padding: .5rem; font-size: .88rem; color: var(--tk-accent); }
  `;

  render() {
    if (this.dismissed || !this.authStatus?.authenticated || this.authStatus?.authMethod !== 'teknik') return html``;
    return html`
      <div class="checklist">
        <div class="checklist-header">
          <span class="checklist-title" style="display:flex;align-items:center;gap:6px"><img src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/icons/check2-circle.svg" style="width:14px;height:14px;filter:brightness(0) saturate(100%) invert(1);opacity:.7" alt="" /> Get started with Teknik</span>
          <div style="display:flex;align-items:center;gap:10px">
            <span class="checklist-prog">${this.progress}/${this.total}</span>
            <button class="dismiss-btn" @click=${() => this.dismiss()} title="Dismiss"><img src="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/icons/x.svg" style="width:14px;height:14px;filter:brightness(0) saturate(100%) invert(1);opacity:.5;vertical-align:middle" alt="Dismiss" /></button>
          </div>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" style="width:${(this.progress / this.total) * 100}%"></div>
        </div>
        ${this.allDone ? html`<div class="all-done">All done.</div>` : html`
          ${this.visibleSteps.map(s => {
            const done = this.completed.includes(s.id);
            return html`
              <div class="step" @click=${() => { if (!done && s.url) window.location.href = s.url; this.markDone(s.id); }}>
                <div class="step-check ${done ? 'done' : ''}"></div>
                <img src="${s.icon}" class="step-icon" style="width:16px;height:16px;filter:brightness(0) saturate(100%) invert(1);opacity:0.6;flex-shrink:0" alt="" />
                <div class="step-info">
                  <div class="step-label ${done ? 'done' : ''}">${s.label}</div>
                  <div class="step-desc">${s.desc}</div>
                </div>
              </div>
            `;
          })}
        `}
      </div>
    `;
  }
}
